import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Loader2, Plus } from 'lucide-react';
import { PodcastEpisodeService } from '@/services/PodcastEpisodeService';
import { StatusBadge } from '@/components/StatusBadge';
import { customToast } from '@/components/ui/custom-toast';
import { useUserCompanies } from '@/contexts/UserCompaniesContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import type { PodcastEpisode, CompanyEpisodeStats } from '@/types/episode.types';

interface PickUpWhereYouLeftOffCardProps {
  companyStats: CompanyEpisodeStats[];
  limit?: number;
}

/**
 * Dashboard card showing episodes the user is actively working on.
 * Displays episodes with their status, next action, and time since last update.
 */
export const PickUpWhereYouLeftOffCard: React.FC<PickUpWhereYouLeftOffCardProps> = ({
  companyStats,
  limit = 3,
}) => {
  const navigate = useNavigate();
  const { userCompanies, getCompanyName, getCompanyColor } = useUserCompanies();
  const [episodes, setEpisodes] = useState<PodcastEpisode[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [creatingEpisode, setCreatingEpisode] = useState(false);

  const isMultiCompany = userCompanies.length > 1;

  // Get company initials (first letter of first two words)
  const getCompanyInitials = useCallback((companyId: string): string => {
    const name = userCompanies.find(c => c.id === companyId)?.name || '';
    if (!name) return '?';
    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      return name.substring(0, 2).toUpperCase();
    }
    return (words[0][0] + words[1][0]).toUpperCase();
  }, [userCompanies]);

  // Fetch in-progress episodes for all companies the user has access to
  const fetchInProgressEpisodes = useCallback(async () => {
    try {
      // Extract company IDs from userCompanies
      const companyIds = userCompanies.map(c => c.id);

      // Fetch episodes for all companies (more than limit to get total count)
      const allEpisodes = await PodcastEpisodeService.getInProgressEpisodes(50, companyIds);
      setTotalCount(allEpisodes.length);
      setEpisodes(allEpisodes.slice(0, limit));
    } catch (error) {
      console.error('Failed to fetch in-progress episodes:', error);
    } finally {
      setLoading(false);
    }
  }, [limit, userCompanies]);

  // Initial fetch - wait for userCompanies to be populated
  useEffect(() => {
    if (userCompanies.length > 0) {
      fetchInProgressEpisodes();
    }
  }, [fetchInProgressEpisodes, userCompanies.length]);

  // Handle row click - navigate to episode detail
  const handleRowClick = (episodeId: string) => {
    navigate(`../episodes/${episodeId}`);
  };

  // Handle "View all" click
  const handleViewAll = () => {
    navigate('../episodes?filter=in-progress');
  };

  // Handle create new episode
  const handleCreateEpisode = useCallback(async (companyId?: string) => {
    try {
      setCreatingEpisode(true);
      const { id } = await PodcastEpisodeService.createDraftEpisode(undefined, companyId);
      customToast.success('Episode created');
      navigate(`../episodes/${id}`);
    } catch (err: any) {
      customToast.error('Failed to create episode', {
        description: err.message || 'Please try again',
      });
      setCreatingEpisode(false);
    }
  }, [navigate]);

  const hasMore = totalCount > limit;

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{
        backgroundColor: 'var(--pm-bg-card)',
        border: '1px solid var(--pm-border-default)',
        minHeight: '280px',
      }}
    >
      {/* Card Header */}
      <div
        className="flex items-center justify-between"
        style={{
          backgroundColor: 'var(--pm-cyan-bg)',
          borderBottom: '1px solid var(--pm-border-default)',
          padding: '14px 20px',
        }}
      >
        <h3
          className="font-semibold uppercase"
          style={{ fontSize: '11px', letterSpacing: '0.5px', color: 'var(--pm-text-faint)' }}
        >
          Pick Up Where You Left Off
        </h3>
        {!loading && episodes.length > 0 && hasMore && (
          <button
            onClick={handleViewAll}
            className="flex items-center gap-1 text-xs transition-colors"
            style={{ color: 'var(--pm-text-muted)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--pm-text-primary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--pm-text-muted)'; }}
          >
            View all
            <ChevronRight className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Card Content - max height to match help callout card, with scroll for overflow */}
      <div style={{ padding: '16px 20px', maxHeight: '400px', overflowY: 'auto' }}>
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--pm-text-faint)' }} />
          </div>
        ) : episodes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8">
            <p className="text-lg mb-8">
              No episodes in progress
            </p>
            {isMultiCompany ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    disabled={creatingEpisode}
                    className="flex items-center gap-2 rounded-lg font-medium transition-colors"
                    style={{
                      backgroundColor: creatingEpisode ? '#00a3cc' : '#00d4ff',
                      color: 'var(--pm-btn-primary-text)',
                      padding: '8px 14px',
                      fontSize: '13px',
                      opacity: creatingEpisode ? 0.7 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (!creatingEpisode) e.currentTarget.style.backgroundColor = '#00b8e6';
                    }}
                    onMouseLeave={(e) => {
                      if (!creatingEpisode) e.currentTarget.style.backgroundColor = '#00d4ff';
                    }}
                  >
                    {creatingEpisode ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                    Create New Episode
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center">
                  {userCompanies.map((company) => (
                    <DropdownMenuItem
                      key={company.id}
                      onClick={() => handleCreateEpisode(company.id)}
                    >
                      <div
                        className="w-3 h-3 rounded-full mr-2"
                        style={{ backgroundColor: getCompanyColor(company.id) || '#4A90E2' }}
                      />
                      {company.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <button
                onClick={() => handleCreateEpisode()}
                disabled={creatingEpisode}
                className="flex items-center gap-2 rounded-lg font-medium transition-colors"
                style={{
                  backgroundColor: creatingEpisode ? '#00a3cc' : '#00d4ff',
                  color: 'var(--pm-btn-primary-text)',
                  padding: '8px 14px',
                  fontSize: '13px',
                  opacity: creatingEpisode ? 0.7 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!creatingEpisode) e.currentTarget.style.backgroundColor = '#00b8e6';
                }}
                onMouseLeave={(e) => {
                  if (!creatingEpisode) e.currentTarget.style.backgroundColor = '#00d4ff';
                }}
              >
                {creatingEpisode ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Create New Episode
              </button>
            )}
          </div>
        ) : (
          <div style={{ borderColor: 'var(--pm-cyan-bg-strong)' }} className="divide-y">
            {episodes.map((episode) => {
              const nextAction = PodcastEpisodeService.getNextAction(episode);
              const timeAgo = PodcastEpisodeService.formatRelativeTime(episode.updatedAt);

              const progress = PodcastEpisodeService.getWorkflowProgress(episode);
              const clipCount = episode.clipCount || 0;

              return (
                <div
                  key={episode.id}
                  onClick={() => handleRowClick(episode.id)}
                  className="py-5 first:pt-0 last:pb-0 -mx-5 px-5 cursor-pointer transition-colors"
                  style={{ backgroundColor: 'transparent' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--pm-cyan-bg-strong)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {/* Top row: Company avatar, Title, Status badge */}
                  <div className="flex items-center gap-3">
                    {/* Company Avatar (multi-company only) */}
                    {isMultiCompany && (
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs font-medium text-white"
                        style={{
                          backgroundColor: `${getCompanyColor(episode.companyId) || '#4A90E2'}80`,
                        }}
                        title={getCompanyName(episode.companyId)}
                      >
                        {getCompanyInitials(episode.companyId)}
                      </div>
                    )}

                    {/* Episode Title */}
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <p className="text-sm font-medium truncate" style={{ color: 'var(--pm-text-primary)' }}>
                        {episode.title}
                      </p>
                    </div>

                    {/* Status Badge */}
                    <StatusBadge status={episode.status} scheduledPublishDate={episode.scheduledPublishDate} />
                  </div>

                  {/* Second row: Clip count, progress bar, next action */}
                  <div className="flex items-center gap-3 mt-2">
                    {/* Clip count */}
                    <span className="flex-shrink-0 text-xs" style={{ color: 'var(--pm-text-muted)' }}>
                      {clipCount} {clipCount === 1 ? 'clip' : 'clips'}
                    </span>

                    {/* Progress bar */}
                    <div className="flex-1 flex items-center gap-2">
                      <div
                        className="flex-1 h-1.5 rounded-full overflow-hidden"
                        style={{ backgroundColor: 'var(--pm-cyan-bg-strong)' }}
                      >
                        <div
                          className="h-full rounded-full transition-all duration-300"
                          style={{
                            width: `${Math.max(0, progress)}%`,
                            backgroundColor: progress < 0 ? '#f87171' : '#22d3ee', // red-400 for failed, cyan-400 for normal
                          }}
                        />
                      </div>
                      <span className="flex-shrink-0 text-xs w-8 text-right" style={{ color: 'var(--pm-text-muted)' }}>
                        {progress >= 0 ? `${progress}%` : '—'}
                      </span>
                    </div>

                    {/* Next Action */}
                    <span className="flex-shrink-0 text-xs hidden sm:block" style={{ color: 'var(--pm-text-muted)' }}>
                      {nextAction}
                    </span>
                  </div>

                  {/* Third row: Created by, creation date, and modified time */}
                  <div className="flex items-center gap-1 mt-2">
                    <span className="text-xs" style={{ color: 'var(--pm-text-faint)' }}>
                      Created by {episode.createdByName || 'Unknown'} · {PodcastEpisodeService.formatRelativeTime(episode.createdAt)} · Modified {timeAgo}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
